import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { SelectWorkflowComponent } from './common/selectcomponent'

class MakeLine extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.makeline',
        group: 'Spatial Constructors',
        title: 'ST Make Line',
        description: `
      This component creates a LineString based on a points column. It will take the points in the order they are in the table.
      `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'points',
          type: WorkflowComponentParamType.Column,
          title: 'Points column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        }
      ]
    )
  }

  getInnerSql({ params, connection }: WorkflowComponentCodeParams) {
    switch (connection.type) {
      case Provider.BigQuery:
        return dedent`SELECT ST_MAKELINE(ARRAY_AGG(${params.points})) geom
                FROM ${connection.quoteName(params.source as string)}`
      case Provider.Postgres:
        return dedent`SELECT ST_MAKELINE(${params.points}) geom
                FROM ${connection.quoteName(params.source as string)}`
      case Provider.Redshift:
        return dedent`SELECT
                  CASE WHEN ST_NPOINTS(ST_COLLECT(${params.points})) <= 1 THEN
                    ST_COLLECT(${params.points})
                  ELSE
                    ST_REMOVEPOINT(
                      ST_MAKELINE(
                        ST_GEOMFROMTEXT('POINT (0 0)', ST_SRID(ST_COLLECT(${params.points}))),
                        ST_COLLECT(${params.points})),
                      0)
                  END geom
                  FROM ${connection.quoteName(params.source as string)}`
      case Provider.DatabricksRest:
        return dedent`SELECT /*+ REPARTITION(40, geom) */
                  ${connection.processOutputGeogColumn(
                    `ST_MAKELINE(ARRAY_AGG(${connection.processInputGeogColumn(params.points as string)}))`
                  )} geom
                FROM ${connection.quoteName(params.source as string)}`
      case Provider.Oracle: {
        const pointsColumn = params.points as string
        const outputColumn = 'geom'
        // Oracle implementation: collect all points and create a linestring
        // Build ordinate array by collecting coordinates in the correct order
        return dedent`WITH ordered_coords AS (
          SELECT
            CASE
              WHEN ${pointsColumn}.SDO_POINT IS NOT NULL THEN ${pointsColumn}.SDO_POINT.X
              ELSE ${pointsColumn}.SDO_ORDINATES(1)
            END AS x_coord,
            CASE
              WHEN ${pointsColumn}.SDO_POINT IS NOT NULL THEN ${pointsColumn}.SDO_POINT.Y
              ELSE ${pointsColumn}.SDO_ORDINATES(2)
            END AS y_coord,
            ${pointsColumn}.SDO_SRID AS srid,
            ROW_NUMBER() OVER (ORDER BY ROWNUM) AS rn
          FROM ${connection.quoteName(params.source as string)}
          WHERE ${pointsColumn} IS NOT NULL
        ),
        flattened_coords AS (
          SELECT
            x_coord AS coord_value,
            (rn - 1) * 2 + 1 AS ord_position,
            srid
          FROM ordered_coords
          UNION ALL
          SELECT
            y_coord AS coord_value,
            (rn - 1) * 2 + 2 AS ord_position,
            srid
          FROM ordered_coords
        ),
        aggregated AS (
          SELECT
            CAST(COLLECT(coord_value ORDER BY ord_position) AS SDO_ORDINATE_ARRAY) AS ordinates,
            MAX(srid) AS srid_value,
            COUNT(DISTINCT ord_position) / 2 AS point_count
          FROM flattened_coords
        )
        SELECT
          CASE
            WHEN point_count >= 2 THEN
              SDO_GEOMETRY(
                2002, -- LINESTRING
                COALESCE(srid_value, 4326),
                NULL,
                SDO_ELEM_INFO_ARRAY(1, 2, 1), -- Simple linestring
                ordinates
              )
            ELSE NULL
          END AS ${outputColumn}
        FROM aggregated`
      }
      default:
        return null
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ connection }: GetOutputSchemaParams): TableSchema {
    return [
      {
        name: 'geom',
        type: connection.getGeoDataType()
      }
    ]
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Postgres, Provider.Redshift, Provider.DatabricksRest, Provider.Oracle]
  }
}

export { MakeLine }
